import { FC, useState } from 'react';
import * as Yup from 'yup';
import { AlertTriangle } from 'lucide-react';
import { Input } from '@components/atoms/Input';
import { Button } from '@components/atoms/Button';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { useTranslation } from '@hooks/useTranslation';

export type LinkProblemReason = 'expired' | 'used' | 'invalid';

export interface LinkProblemPanelProps {
  reason: LinkProblemReason;
  onResend: (email: string) => void;
  isSending: boolean;
  /** Rendered under the button, for example a rate-limit sentence. */
  errorMessage?: string | null;
  onGoToSignIn: () => void;
}

const emailSchema = Yup.string().email().required();

/**
 * Sprint 47, story-232. EXPIRED / USED / INVALID link states. No email is known for these (verify
 * returns it only when VALID), so the panel asks for one and offers "Send me a new link". The
 * resend outcome is the same whatever is typed (no enumeration).
 */
export const LinkProblemPanel: FC<LinkProblemPanelProps> = ({
  reason,
  onResend,
  isSending,
  errorMessage,
  onGoToSignIn,
}) => {
  const tSetup = useTranslation('setup');
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);

  const titles = {
    expired: tSetup.expiredTitle,
    used: tSetup.usedTitle,
    invalid: tSetup.invalidTitle,
  } as const;
  const bodies = {
    expired: tSetup.expiredBody,
    used: tSetup.usedBody,
    invalid: tSetup.invalidBody,
  } as const;

  const emailInvalid = touched && !emailSchema.isValidSync(email.trim());

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (!emailSchema.isValidSync(email.trim())) return;
    onResend(email.trim());
  };

  return (
    <div className="flex flex-col gap-4" data-testid={`setup-link-${reason}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle
          aria-hidden="true"
          className="mt-1 h-5 w-5 shrink-0 text-amber-500"
        />
        <h1
          tabIndex={-1}
          className="text-xl font-bold text-gray-900 dark:text-white">
          {titles[reason]}
        </h1>
      </div>
      <p className="text-gray-600 dark:text-gray-300">{bodies[reason]}</p>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        <FormGroup
          label={tSetup.emailLabel}
          htmlFor="setup-resend-email"
          error={emailInvalid ? tSetup.emailInvalid : undefined}>
          <Input
            id="setup-resend-email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder={tSetup.emailPlaceholder}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            onBlur={() => setTouched(true)}
            error={emailInvalid}
          />
        </FormGroup>
        {errorMessage && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {errorMessage}
          </p>
        )}
        <Button
          fullWidth
          type="submit"
          variant="primary"
          loading={isSending}
          disabled={isSending}>
          {tSetup.resend}
        </Button>
      </form>
      {reason !== 'expired' && (
        <button
          type="button"
          onClick={onGoToSignIn}
          className="text-sm text-indigo-600 hover:underline dark:text-indigo-300">
          {tSetup.goToSignIn}
        </button>
      )}
    </div>
  );
};

export default LinkProblemPanel;
